import { useMemo } from "react";

import { useLocation, useSearchParams } from "react-router-dom";
import usePublisherKyc from "../../hooks/use-publisher-kyc";
import { PUBLISHER_TABS } from "../../misc/constants";
import { brandOfPublisher, toKycSummary, uniqueBrands } from "../../misc/kyc";
import usePublisherInvoices from "./use-publisher-invoices";
import usePublisherKycRecord from "./use-publisher-kyc-record";

const usePublisherDetails = (publisherId) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // The list row the page was opened from (payout, earnings) under the KYC record,
    // which is fetched fresh so a pasted link works too. The record's own `_id` is the
    // KYC document, not the publisher — keep the route's id so actions hit the right one.
    const row = location.state?.publisher;
    const origin = location.state?.from;
    const kycRecord = usePublisherKycRecord(publisherId);
    const publisher = useMemo(() => {
        if (!row && !kycRecord.record) return null;
        const merged = { ...row, ...kycRecord.record, _id: publisherId };
        // A record without a status is one the publisher never started.
        return { ...merged, kyc_status: merged.kyc_status || "not_started" };
    }, [row, kycRecord.record, publisherId]);

    const invoicesTab = usePublisherInvoices(publisherId);
    const originBrand = origin?.id ? { id: origin.id, name: origin.label } : undefined;
    const kyc = usePublisherKyc({ brand: originBrand, onChanged: kycRecord.refetch });

    // Every brand this publisher is known to work with. The brand they were opened
    // from comes first so it's the reminder's default sender.
    const linkedBrands = useMemo(
        () => uniqueBrands([
            originBrand,
            brandOfPublisher(publisher),
            ...invoicesTab.invoices.map((inv) => ({ id: inv.account_id, name: inv.account_name }))
        ]),
        [origin?.id, publisher, invoicesTab.invoices]
    );

    // `?tab=` wins so a tab can be linked and survives a refresh; otherwise the first tab.
    const requestedTab = searchParams.get("tab");
    const activeTab = PUBLISHER_TABS.some((tab) => tab.id === requestedTab) ? requestedTab : PUBLISHER_TABS[0].id;

    // Keep the navigation state (publisher row + origin) when switching tabs.
    const setActiveTab = (tab) => setSearchParams({ tab }, { replace: true, state: location.state });

    // Opened from a brand's Publishers tab → lead back there (on that tab); otherwise
    // back to the cross-brand list.
    const crumbs = origin
        ? [{ label: "Brands", to: "/brands" }, { label: origin.label || "Brand", to: origin.tab ? `${origin.to}?tab=${origin.tab}` : origin.to }]
        : [{ label: "Publishers", to: "/publishers" }];

    const brands = useMemo(
        () => [...new Set([publisher?.brand || publisher?.account_name, ...linkedBrands.map((brand) => brand.name)].filter(Boolean))],
        [publisher, linkedBrands]
    );

    return {
        publisher,
        publisherName: publisher?.holder_name || invoicesTab.invoices[0]?.holder_name || "Publisher",
        email        : publisher?.email || invoicesTab.invoices[0]?.email,
        brands,
        crumbs,
        activeTab,
        setActiveTab,
        tabCounts    : { invoices: invoicesTab.stats?.total_invoices ?? invoicesTab.invoices.length },
        kyc,
        kycTab       : {
            publisher,
            brands,
            summary        : publisher ? toKycSummary(publisher) : null,
            loading        : kycRecord.loading && !kycRecord.record,
            onRetry        : kycRecord.refetch,
            canSendReminder: Boolean(publisher) && kyc.canSendFor(publisher, linkedBrands),
            onRemind       : () => kyc.openReminder(publisher, linkedBrands),
            onForceVerify  : () => kyc.forceVerify(publisher),
            verifying      : kyc.verifying
        },
        invoicesTab
    };
};

export default usePublisherDetails;
